import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock, CheckCircle, XCircle, FileText, Shield, Building2, Edit } from 'lucide-react';
import { Card, Button } from '../components';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export const ScreeningPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('screening');

  // Poll screening status every 30 seconds
  const { data: statusData, isLoading } = useQuery({
    queryKey: ['screening-status'],
    queryFn: async () => {
      const response = await api.get('/screening/status');
      return response.data;
    },
    refetchInterval: 30000, // 30 seconds
  });

  const status = statusData?.data;

  // Redirect to wizard if no screening exists yet
  useEffect(() => {
    if (status?.should_start_wizard) {
      navigate('/professional/screening');
    }
  }, [status, navigate]);

  // Redirect based on status. Fast-track: contract tekenen en profiel
  // inrichten kunnen al tijdens 'pending' — alleen doorsturen zodra de
  // screening daadwerkelijk is goedgekeurd, anders blijft de statuspagina
  // bereikbaar.
  useEffect(() => {
    if (status && status.screening?.status === 'approved') {
      if (status.onboarding_step === 'screening_approved' || status.onboarding_step === 'contract_pending') {
        navigate('/professional/contract');
      } else if (status.onboarding_step === 'contract_signed') {
        navigate('/professional/profile');
      } else if (status.onboarding_step === 'profile_complete') {
        navigate('/professional/dashboard');
      }
    }
  }, [status, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('info.loadingData')}</p>
        </div>
      </div>
    );
  }

  const screeningStatus = status?.screening?.status;
  const isPending = screeningStatus === 'pending';
  const isApproved = screeningStatus === 'approved';
  const isRejected = screeningStatus === 'rejected';

  // If no screening exists, show message
  if (!status?.screening && !isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <div className="text-center">
            <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('pending.noScreeningTitle')}</h2>
            <p className="text-gray-600 mb-6">
              {t('pending.noScreeningDesc')}
            </p>
            <Button onClick={() => navigate('/professional/screening')}>
              {t('pending.startScreening')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <img
            src="/belagom-text.png"
            alt="BeLagom"
            className="h-12 mx-auto cursor-pointer mb-6"
            onClick={() => navigate('/')}
          />
        </div>

        <Card>
          {isPending && (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
                  <Clock className="h-8 w-8 text-yellow-600 animate-pulse" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {t('pending.title')}
                </h1>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FileText className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      {t('pending.processingInfo')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Fast-track: professional hoeft niet te wachten op de handmatige controle */}
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-semibold text-green-800 mb-1">
                      {t('pending.fastTrackTitle')}
                    </h3>
                    <p className="text-sm text-green-700 mb-3">
                      {t('pending.fastTrackDesc')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button size="sm" onClick={() => navigate('/professional/contract')}>
                        {t('pending.signAgreementNow')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => navigate('/professional/profile')}>
                        {t('pending.setupProfileNow')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h2 className="font-semibold text-gray-900">{t('pending.submittedDocuments')}:</h2>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start">
                    <Building2 className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">{t('label.kvkExtractPdf')}</p>
                      <p className="text-sm text-gray-600">
                        {status?.screening?.kvk_nummer && `KvK: ${status.screening.kvk_nummer}`}
                        {status?.screening?.bedrijfsnaam && ` - ${status.screening.bedrijfsnaam}`}
                      </p>
                    </div>
                  </div>

                  {status?.screening?.selected_professional_types && (
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">{t('pending.selectedFunctions')}</p>
                        <p className="text-sm text-gray-600">
                          {(Array.isArray(status.screening.selected_professional_types)
                            ? status.screening.selected_professional_types
                            : JSON.parse(status.screening.selected_professional_types)
                          ).join(', ')}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start">
                    <Shield className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">{t('label.insurancePolicyPdf')}</p>
                      <p className="text-sm text-gray-600">
                        {status?.screening?.insurance_company && `${status.screening.insurance_company} - `}
                        {status?.screening?.coverage_amount && `€${Number(status.screening.coverage_amount).toLocaleString('nl-NL')} ${t('pending.coverage')}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center space-y-4">
                <p className="text-sm text-gray-500">
                  {t('pending.autoRefreshNote')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => navigate('/professional/screening')} className="flex items-center justify-center">
                    <Edit className="w-4 h-4 mr-2" />
                    {t('pending.adjustScreening')}
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/professional/cockpit')}>
                    {t('pending.toDashboard')}
                  </Button>
                </div>
              </div>
            </>
          )}

          {isRejected && (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {t('pending.rejectedTitle')}
                </h1>
                <p className="text-lg text-gray-600">
                  {t('pending.rejectedSubtitle')}
                </p>
              </div>

              {(status?.screening?.rejection_reason || status?.screening?.admin_feedback) && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <XCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      {status?.screening?.rejection_reason && (
                        <>
                          <h3 className="text-sm font-medium text-red-800">{t('pending.rejectionReasonTitle')}:</h3>
                          <p className="mt-1 text-sm font-semibold text-red-700">
                            {t(`pending.rejectReasons.${status.screening.rejection_reason}`, { defaultValue: status.screening.rejection_reason }) as string}
                          </p>
                        </>
                      )}
                      {status?.screening?.admin_feedback && (
                        <>
                          <h3 className={`text-sm font-medium text-red-800 ${status?.screening?.rejection_reason ? 'mt-3' : ''}`}>{t('pending.feedbackTitle')}:</h3>
                          <div className="mt-1 text-sm text-red-700 italic">
                            <p>"{status.screening.admin_feedback}"</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 text-center space-y-4">
                <p className="text-gray-600">
                  {t('pending.rejectedDesc')}
                </p>
                <Button onClick={() => navigate('/professional/screening')}>
                  {t('pending.fixScreening')}
                </Button>
              </div>
            </>
          )}

          {isApproved && (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {t('pending.approvedTitle')}
                </h1>
                <p className="text-lg text-gray-600">
                  {t('pending.approvedSubtitle')}
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      {t('pending.approvedDesc')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Button onClick={() => navigate('/professional/contract')} size="lg">
                  {t('pending.signContract')}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
